import axiosInstance from './axiosInstance';
import { IDocument } from '../interfaces/Document/IDocument.ts';
import { IDocumentListResponse } from '../interfaces/Document/IDocumentListResponse.ts';
import { IDocumentListParams } from '../interfaces/Document/IDocumentListParams.ts';
import { IDocumentUpload } from '../interfaces/Document/IDocumentUpload.ts';
import { AxiosProgressEvent } from 'axios';

interface UpdateDocumentParams {
  carehome_id?: string | null;
  organisation_id?: string | null;
  tag?:
    | 'Contract Document'
    | 'Template or Form'
    | 'Product Information'
    | 'My Document';
}

class DocumentService {
  private static instance: DocumentService;
  private constructor() {}

  public static getInstance(): DocumentService {
    if (!DocumentService.instance) {
      DocumentService.instance = new DocumentService();
    }
    return DocumentService.instance;
  }

  public async uploadDocument(
    file: File,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void
  ): Promise<IDocumentUpload> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post<IDocumentUpload>(
        `/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: onUploadProgress,
        }
      );

      return response.data;
    } catch (error) {
      throw new Error('Failed to create document');
    }
  }

  public async getDocuments(
    params?: IDocumentListParams
  ): Promise<IDocumentListResponse> {
    try {
      const response = await axiosInstance.get<IDocumentListResponse>(
        '/documents',
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch documents');
    }
  }

  public async getDocument(documentId: string): Promise<IDocument> {
    try {
      const response = await axiosInstance.get<IDocument>(
        `/documents/${documentId}`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch document');
    }
  }

  public async updateDocument(
    documentId: string,
    params: UpdateDocumentParams
  ): Promise<IDocument> {
    try {
      const response = await axiosInstance.put<IDocument>(
        `/documents/${documentId}`,
        params
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update document');
    }
  }

  public async deleteDocument(documentId: string): Promise<void> {
    try {
      await axiosInstance.delete(`/documents/${documentId}`);
    } catch (error) {
      throw new Error('Failed to delete document');
    }
  }

  public async fetchAvailableTags(): Promise<string[]> {
    try {
      const response = await axiosInstance.get<string[]>(
        '/documents/available_tags'
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch available tags');
    }
  }
}

export default DocumentService.getInstance();
